import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getHelpTopic } from '@emr/shared';

/**
 * The small, quiet, deep-linking affordance beside a control someone has already reached and gotten
 * stuck on (`claude-kit/rules/invariants.md`'s in-app-help section, `HelpLink`). A real `<Link>` —
 * not a `<button>` — so it supports middle-click and right-click-copy the ordinary way a link does.
 * Visually understated on purpose: an outlined glyph in muted ink, not a chip competing for
 * attention, and its accessible name states the actual topic ("Help: signing in") rather than
 * announcing the glyph itself ("question mark") to a screen reader.
 *
 * `topic.body` is not duplicated here — this renders nothing but a link to `/help/:topicId`, so the
 * one place the explanation's text actually lives stays `@emr/shared`'s `HELP_TOPICS`
 * (`invariants.md`'s "nothing is written twice"). `topic.summary` becomes the link's native
 * `title` tooltip, which is the closest thing to "a compact inline disclosure" a plain anchor can
 * offer without inventing a second popover component.
 *
 * Renders nothing for an id that does not match any topic, rather than a broken link or a thrown
 * error — the same stale-link tolerance `HelpPage.tsx`'s own deep-link fallback applies, extended to
 * the id never having been valid in the first place (a typo at the call site).
 */
export function HelpLink({ topicId }: { readonly topicId: string }) {
  const { t } = useTranslation('help');
  const topic = getHelpTopic(topicId);
  if (topic === null) return null;

  // `defaultValue` falls back to the English source (`@emr/shared`'s `HELP_TOPICS`) for a topic
  // `help.json` has no translation for yet — never a raw, untranslated i18next key string.
  const title = t(`topics.${topic.id}.title`, { defaultValue: topic.title });
  const summary = t(`topics.${topic.id}.summary`, { defaultValue: topic.summary });

  return (
    <Link
      to={`/help/${topic.id}`}
      title={summary}
      aria-label={t('helpLink.ariaLabel', { title })}
      className="inline-flex size-5 shrink-0 items-center justify-center rounded-pill border border-line text-xs font-semibold text-ink-muted hover:border-line-strong hover:text-ink"
    >
      <span aria-hidden="true">?</span>
    </Link>
  );
}
